/*
 * CYCLIQ FIT (/fit) : questionnaire de qualification commerciale, séparé
 * du diagnostic. 4 questions, score calculé localement et JAMAIS affiché.
 * AUCUN tarif n'apparaît dans le parcours : l'investissement est annoncé
 * uniquement pendant l'appel avec Gino.
 *
 * Interaction avec CYCLIQ SAFETY : si la branche prudence est active,
 * le CTA commercial immédiat est remplacé par un message de prudence.
 * Le safety_flag n'influence jamais le score FIT et n'est jamais transmis.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, CalendarCheck, Check, Leaf, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EmailDialog } from "@/components/bilan/EmailDialog";
import { fetchPublicSettings, fetchQuizDefinition, trackEvent, syncFit } from "@/lib/api";
import { loadQuizState, loadPrenom, getJourneyId, getUtmParams } from "@/lib/quizStorage";
import { buildResult } from "@/lib/scoringEngine";
import { FIT_QUESTIONS, computeFitScore, determineFitStatus, buildFitSyncPayload } from "@/lib/fitEngine";
import { buildBookingLink } from "@/lib/bilanActions";
import { devWarn } from "@/lib/logger";

const FitChoice = ({ question, selected, onChoice }) => (
  <div className="mt-7 grid grid-cols-1 gap-3" role="group" aria-label={question.text}>
    {question.options.map((option) => {
      const isSelected = selected === option.value;
      return (
        <button
          key={option.value}
          type="button"
          data-testid="fit-answer-option"
          data-value={option.value}
          onClick={() => onChoice(option.value)}
          aria-pressed={isSelected}
          className={`min-h-[56px] w-full rounded-2xl border px-4 py-3.5 text-left text-sm sm:text-base font-medium transition-colors duration-150 active:scale-[0.99] flex items-center justify-between gap-3 ${
            isSelected
              ? "border-[#235277] ring-2 ring-[#EAFE63] bg-[#E2F6FF] text-[#0B2B44]"
              : "border-[rgba(35,82,119,0.16)] bg-white text-[#333333] hover:border-[rgba(35,82,119,0.4)] hover:bg-[rgba(226,246,255,0.4)]"
          }`}
        >
          <span>{option.label}</span>
          {isSelected && <Check className="w-5 h-5 shrink-0 text-[#235277]" aria-hidden="true" />}
        </button>
      );
    })}
  </div>
);

const FitScale = ({ question, value, onChange, onContinue }) => (
  <div className="mt-10">
    <div className="text-center mb-8">
      <span className="font-heading inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#235277] text-[#EAFE63] text-2xl font-bold tabular-nums" data-testid="fit-scale-value">
        {value}
      </span>
    </div>
    <Slider
      data-testid="fit-scale-slider"
      value={[value]}
      onValueChange={(values) => onChange(values[0])}
      min={question.min}
      max={question.max}
      step={1}
      aria-label={question.text}
    />
    <div className="mt-3 flex justify-between text-xs text-[#9E9E9E]">
      <span>{question.min_label}</span>
      <span>{question.max_label}</span>
    </div>
    <Button
      data-testid="fit-scale-continue-button"
      onClick={onContinue}
      className="mt-8 w-full min-h-[52px] rounded-xl bg-[#235277] hover:bg-[#1E4767] text-white font-heading font-semibold active:scale-[0.98] transition-colors duration-200"
    >
      Continuer
    </Button>
  </div>
);

/* Écrans de résultat : le score FIT n'est JAMAIS affiché. */

const CautionOutcome = () => (
  <div className="rounded-2xl bg-[#FDF6E7] border border-[rgba(94,85,21,0.3)] p-6 text-left" data-testid="fit-outcome-caution">
    <div className="flex items-center gap-2">
      <ShieldAlert className="w-4 h-4 text-[#5E5515]" aria-hidden="true" />
      <h2 className="font-heading text-base font-semibold text-[#5E5515]">Avant toute chose, ta sécurité</h2>
    </div>
    <p className="mt-3 text-sm leading-relaxed text-[#5E5515]">
      Merci pour tes réponses. Comme tu l'as indiqué pendant le diagnostic, il est préférable de
      clarifier ta situation avec le professionnel de santé qui te suit avant d'envisager un
      accompagnement incluant une pratique physique. Quand ta situation sera clarifiée, tu seras
      la bienvenue pour échanger avec Gino sur les conditions adaptées.
    </p>
  </div>
);

const BookingOutcome = ({ status, onBooking }) => (
  <div className="rounded-2xl bg-[#E2F6FF] border border-[rgba(35,82,119,0.16)] p-6 sm:p-8 text-center" data-testid={`fit-outcome-${status === "high_priority" ? "high" : "qualified"}`}>
    <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#060606]">Ton prochain pas</h2>
    <p className="mt-3 text-sm leading-relaxed text-[#333333] max-w-[520px] mx-auto" data-testid="fit-outcome-message">
      {status === "high_priority"
        ? "Ton bilan t'a permis de mieux comprendre certaines tendances de ton fonctionnement. Si tu souhaites maintenant savoir comment transformer ces observations en stratégie personnalisée, tu peux réserver un échange avec Gino. Cet entretien permettra de vérifier si CYCLIQ EXCELLENCE correspond réellement à tes besoins et à tes objectifs."
        : "Tes réponses semblent compatibles avec un échange exploratoire. Tu peux réserver un entretien avec Gino afin de vérifier, sans engagement, si CYCLIQ EXCELLENCE correspond réellement à tes attentes et à ta situation."}
    </p>
    <div className="mt-6 flex justify-center">
      <Button
        data-testid="fit-book-call-button"
        onClick={onBooking}
        className="min-h-[52px] px-7 rounded-xl bg-[#235277] hover:bg-[#1E4767] text-white font-heading font-semibold text-base active:scale-[0.98] shadow-[var(--shadow-cta)] transition-colors duration-200"
      >
        <CalendarCheck className="mr-2 w-4 h-4" aria-hidden="true" />
        Réserver mon échange CYCLIQ
      </Button>
    </div>
  </div>
);

const NurtureOutcome = ({ result, settings, prenom, onPrenomChange }) => (
  <div className="rounded-2xl bg-white border border-[rgba(35,82,119,0.16)] p-6 sm:p-8 text-center" data-testid="fit-outcome-nurture">
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#EAF7E7] text-[#114E0B]" aria-hidden="true">
      <Leaf className="h-5 w-5" />
    </div>
    <h2 className="font-heading mt-4 text-xl font-semibold tracking-tight text-[#060606]">Continue à ton rythme</h2>
    <p className="mt-3 text-sm leading-relaxed text-[#333333] max-w-[520px] mx-auto" data-testid="fit-nurture-message">
      CYCLIQ EXCELLENCE n'est peut être pas la prochaine étape la plus adaptée aujourd'hui.
      Continue néanmoins à observer ton fonctionnement et à construire des habitudes qui
      soutiennent ton énergie et ta santé. Lorsque ta situation ou tes priorités évolueront,
      tu pourras refaire le diagnostic.
    </p>
    <p className="mt-3 text-xs leading-relaxed text-[#9E9E9E] max-w-[520px] mx-auto">
      Pour recevoir les conseils CYCLIQ par email, demande ton bilan ci dessous et coche la case
      des conseils personnalisés.
    </p>
    <div className="mt-5 flex justify-center">
      <EmailDialog
        result={result}
        settings={settings}
        prenomInitial={prenom}
        onPrenomChange={onPrenomChange}
        triggerLabel="Recevoir les conseils CYCLIQ"
      />
    </div>
  </div>
);

export default function FitPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [definition, setDefinition] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [scaleValue, setScaleValue] = useState(5);
  const [outcome, setOutcome] = useState(null);
  const [prenom, setPrenom] = useState(loadPrenom());
  const startedTracked = useRef(false);

  const saved = useMemo(() => loadQuizState(), []);
  const quizCompleted = Boolean(saved && saved.completed && saved.answers && Object.keys(saved.answers).length > 0);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!quizCompleted) {
      navigate("/", { replace: true });
      return;
    }
    if (!startedTracked.current) {
      startedTracked.current = true;
      trackEvent("fit_started");
    }
    fetchPublicSettings().then(setSettings).catch(() => setSettings(null));
    fetchQuizDefinition().then(setDefinition).catch(() => setDefinition(null));
  }, [quizCompleted, navigate]);

  /* Bilan local : utilisé UNIQUEMENT pour la branche prudence et le dialogue
     email. Aucun élément du bilan n'entre dans le score FIT. */
  const result = useMemo(() => {
    if (!quizCompleted) return null;
    try {
      return buildResult(saved.answers, {
        actionsLibrary: settings?.actions_library || null,
        scoringConfig: settings?.scoring_config || null,
        skipRules: definition?.skip_rules || undefined,
      });
    } catch (error) {
      devWarn("[cycliq] calcul local impossible sur la page FIT :", error?.name || "erreur inconnue");
      return null;
    }
  }, [quizCompleted, saved, settings, definition]);

  if (!quizCompleted) return null;

  // Seuils d'orientation commerciale (administrables). Aucun tarif ici :
  // l'investissement est annoncé uniquement pendant l'appel.
  const fitConfig = settings?.fit_config || { threshold_high: 75, threshold_qualified: 55 };
  const question = FIT_QUESTIONS[stepIndex];
  const medicalCaution = Boolean(result?.flags?.medical_caution);

  const finalize = (finalAnswers) => {
    const score = computeFitScore(finalAnswers);
    const status = determineFitStatus(score, finalAnswers, {
      high: fitConfig.threshold_high, qualified: fitConfig.threshold_qualified,
    });
    trackEvent("fit_completed");
    if (status === "nurture") trackEvent("lead_nurture");
    // Enrichissement CRM : données commerciales uniquement, jamais bloquant.
    syncFit(buildFitSyncPayload(getJourneyId(), finalAnswers, score, status, getUtmParams()));
    setOutcome(status);
  };

  const advance = (value) => {
    const nextAnswers = { ...answers, [question.id]: value };
    setAnswers(nextAnswers);
    if (stepIndex + 1 < FIT_QUESTIONS.length) {
      setStepIndex(stepIndex + 1);
      setScaleValue(5);
      window.scrollTo(0, 0);
    } else {
      finalize(nextAnswers);
    }
  };

  const goBack = () => {
    if (stepIndex === 0) {
      navigate("/bilan");
    } else {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleBooking = () => {
    trackEvent("calendar_viewed");
    window.open(buildBookingLink(settings?.booking_url || "#", getJourneyId()), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="fit-page">
      <Header />
      <main className="flex-1 max-w-[640px] w-full mx-auto px-4 sm:px-6 pt-8 pb-16">
        {outcome === null ? (
          <>
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                data-testid="fit-back-button"
                onClick={goBack}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#235277] hover:text-[#1B415F] transition-colors duration-150"
              >
                <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
                Retour
              </button>
              <span className="text-xs font-semibold text-[#9E9E9E] tabular-nums" data-testid="fit-progress-label">
                Question {stepIndex + 1} sur {FIT_QUESTIONS.length}
              </span>
            </div>
            <Progress value={((stepIndex + 1) / FIT_QUESTIONS.length) * 100} className="mt-3 h-1.5" aria-label="Progression du questionnaire" />

            <AnimatePresence mode="wait">
              <motion.div
                key={question.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <p className="mt-8 text-xs font-semibold tracking-[0.18em] uppercase text-[#235277]">
                  Vérifions ensemble, sans engagement
                </p>
                <h1 className="font-heading mt-4 text-xl sm:text-2xl font-semibold leading-snug text-[#060606]" data-testid="fit-question-text">
                  {question.text}
                </h1>
                {question.type === "choice" ? (
                  <FitChoice question={question} selected={answers[question.id]} onChoice={advance} />
                ) : (
                  <FitScale question={question} value={scaleValue} onChange={setScaleValue} onContinue={() => advance(scaleValue)} />
                )}
              </motion.div>
            </AnimatePresence>
          </>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="pt-4">
            {medicalCaution ? (
              <CautionOutcome />
            ) : outcome === "nurture" ? (
              <NurtureOutcome result={result} settings={settings} prenom={prenom} onPrenomChange={setPrenom} />
            ) : (
              <BookingOutcome status={outcome} onBooking={handleBooking} />
            )}
            <div className="mt-6 text-center">
              <button
                type="button"
                data-testid="fit-back-to-bilan-link"
                onClick={() => navigate("/bilan")}
                className="text-xs font-medium text-[#235277] underline underline-offset-2 hover:text-[#1B415F] transition-colors duration-150"
              >
                Revenir à mon bilan
              </button>
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
}
